import React, { useState, useEffect, ReactNode } from 'react';
import { AuthContext } from './AuthContextCore';
import { User, BusinessSummary, Impersonation, AuthResult, RegisterResult } from './AuthTypes';
import { AUTH_ENDPOINTS, businessApi, getActiveBusinessId, setActiveBusinessId, setBusinessAccessDeniedHandler } from '../config/api';
import { ApiError, errorMessage, requestJson, SERVER_ERROR_MESSAGE } from '../config/apiError';
import { getApiOrigin } from '../config/platform';
import { clearAll as clearPageCache } from '../utils/pageCache';

// Session tracking helpers (no credentials stored client-side)
const sessionStore = {
  // Session indicator — lightweight flag so we know whether to attempt auth check
  setSessionActive: (): void => {
    localStorage.setItem('has_session', '1');
  },
  hasSession: (): boolean => {
    return localStorage.getItem('has_session') === '1';
  },
  clearSession: (): void => {
    localStorage.removeItem('has_session');
  },
  // Migration: clear any legacy credential storage from previous versions
  clearLegacyCredentials: (): void => {
    localStorage.removeItem('_auth_creds');
    localStorage.removeItem('tempCredentials');
  }
};

// What GET /api/auth/user and the login/register responses carry about the user.
interface UserPayload {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  userType: number;
  profilePictureUrl?: string;
  activeBusinessId?: string;
  emailConfirmed?: boolean;
  impersonation?: Impersonation;
}

// Login/register respond with `{ user: {...} }`; older responses put the fields at the top level.
type AuthResponsePayload = UserPayload & { user?: UserPayload };

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // The start-up auth check couldn't get an answer (5xx / network / timeout) for someone who has
  // a session — "unknown", not "signed out". ProtectedRoute shows a Retry screen for it.
  const [authUnavailable, setAuthUnavailable] = useState(false);
  const [businesses, setBusinesses] = useState<BusinessSummary[]>([]);
  const [activeBusiness, setActiveBusiness] = useState<BusinessSummary | null>(null);
  const [impersonation, setImpersonation] = useState<Impersonation>({ active: false, by: null, readOnly: false });

  // Check authentication status on page load
  useEffect(() => {
    // Clear any legacy credential storage from previous versions
    sessionStore.clearLegacyCredentials();
    checkAuthStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Let the api module report "the business id we sent was rejected" (a genuine
  // business-access-denied 403 — see fetchWithAuth's responseBodyParsed check in config/api.ts)
  // without importing React context into it. Recovery re-resolves via fetchBusinesses, passing
  // the id that was just rejected: fetchBusinesses keeps it if the server still lists it
  // (protects against any residual misfire of the trigger) and only falls back to the first
  // business if it's genuinely gone from the list.
  useEffect(() => {
    setBusinessAccessDeniedHandler(() => {
      // Read the id from the api module (the single source of truth the failing request
      // actually sent) rather than the `activeBusiness` React state, which this effect's
      // closure would otherwise capture stale (empty deps — it only registers once on mount).
      const previousId = getActiveBusinessId();
      // Clear before re-fetching so the recovery's own getMyBusinesses() call can't carry the
      // rejected id and re-trigger this handler.
      setActiveBusinessId(null);
      void fetchBusinesses(previousId ?? undefined);
    });
    return () => setBusinessAccessDeniedHandler(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // With HttpOnly cookies, we don't need to get the token from storage
  // The cookie will be automatically sent with requests
  const getToken = (): string | null => {
    // Return null since we can't access HttpOnly cookies from JavaScript
    return null;
  };

  // Function to handle authentication status check (single attempt)
  const checkAuthStatus = async (): Promise<void> => {
    // Skip the network request if there's no reason to think the user has a session.
    // This prevents a 401 error in the browser console on public pages.
    if (!sessionStore.hasSession()) {
      setUser(null);
      setIsAuthenticated(false);
      setAuthUnavailable(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    setAuthUnavailable(false);
    await fetchUser({ startup: true });
  };

  // Fetch the signed-in user. Goes through requestJson so a hung server times out instead of
  // leaving the app on its loading screen indefinitely.
  // `startup`: this is the check that decides whether a protected page may render at all, so a
  // failure to get an answer marks auth as unavailable rather than leaving it "signed out".
  const fetchUser = async ({ startup = false }: { startup?: boolean } = {}): Promise<void> => {
    try {
      const userData = await requestJson<UserPayload>(AUTH_ENDPOINTS.USER, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        credentials: 'include', // Include cookies in the request
        mode: 'cors',
        cache: 'no-store' // Prevent caching of auth requests
      });

      // Add cache-busting parameter to profile picture URL if present
      let profilePicUrl = userData.profilePictureUrl;
      if (profilePicUrl) {
        // Add a timestamp query parameter to force browser to reload the image
        profilePicUrl = `${profilePicUrl}${profilePicUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
      }

      sessionStore.setSessionActive();
      setUser({
        id: userData.id,
        email: userData.email,
        firstName: userData.firstName,
        lastName: userData.lastName,
        userType: userData.userType,
        profilePictureUrl: profilePicUrl,
        activeBusinessId: userData.activeBusinessId,
        emailConfirmed: userData.emailConfirmed
      });
      setImpersonation(userData.impersonation ?? { active: false, by: null, readOnly: false });
      setIsAuthenticated(true);
      setAuthUnavailable(false);
      setLoading(false);
      // Fetch businesses in the background (non-blocking)
      fetchBusinesses(userData.activeBusinessId);
    } catch (err) {
      const status = err instanceof ApiError ? err.status : undefined;
      if (status === 401) {
        // Session expired — user must re-login
        sessionStore.clearSession();
        setUser(null);
        setIsAuthenticated(false);
      } else if (status !== undefined && status < 500) {
        // Other authentication failures (4xx)
        setUser(null);
        setIsAuthenticated(false);
      }
      // A 5xx, a network failure or a timeout leaves the authentication state alone: the server
      // might be starting up, and this prevents redirecting a signed-in user to sign in. At
      // start-up there is no state to leave alone, so it is recorded as unknown (Retry screen).
      else if (startup) {
        setAuthUnavailable(true);
      }
      setLoading(false);
    }
  };

  // Fetch the list of businesses for the authenticated user
  const fetchBusinesses = async (activeBusinessId?: string): Promise<void> => {
    try {
      const list = await businessApi.getMyBusinesses();
      setBusinesses(list);
      // Set active business based on activeBusinessId
      if (list.length > 0) {
        const active = activeBusinessId ? list.find(b => b.id === activeBusinessId) : null;
        const resolved = active || list[0];
        setActiveBusiness(resolved);
        // Push into the api module so subsequent requests carry X-Business-Id, and persist so
        // a page refresh doesn't lose the selection.
        setActiveBusinessId(resolved.id);
      } else {
        setActiveBusiness(null);
        setActiveBusinessId(null);
      }
    } catch {
      // Non-critical — businesses list just won't be populated
    }
  };

  // Switch the active business
  const switchBusiness = async (businessId: string): Promise<void> => {
    const previousBusinessId = activeBusiness?.id ?? null;
    try {
      // Set before the request so that if any other request fires during the switch (or the
      // reload's re-fetch races the response), it already carries the new X-Business-Id.
      setActiveBusinessId(businessId);
      await businessApi.switchBusiness(businessId);
      // Update local state
      if (user) {
        setUser({ ...user, activeBusinessId: businessId });
      }
      const target = businesses.find(b => b.id === businessId);
      if (target) {
        setActiveBusiness(target);
      }
      // Trigger a full page reload so all components re-fetch their data for the new business
      window.location.reload();
    } catch (err) {
      console.error('Failed to switch business:', err);
      // The switch didn't take server-side — don't leave the client pointed at a business id
      // it never actually confirmed.
      setActiveBusinessId(previousBusinessId);
    }
  };

  // The user inside a login/register response body (both shapes the server has used), or null
  // when the body isn't one — e.g. an HTML page or an empty 2xx from something in between.
  const userFrom = (data: AuthResponsePayload | undefined): UserPayload | null => {
    if (!data || typeof data !== 'object') return null;
    const payload = data.user ?? data;
    return typeof payload.id === 'string' && payload.id && typeof payload.email === 'string' ? payload : null;
  };

  // Adopts a validated user. The session flag is only set once the payload has checked out.
  const adoptUser = (payload: UserPayload) => {
    sessionStore.setSessionActive();
    // Add cache-busting parameter to profile picture URL if present
    let profilePicUrl = payload.profilePictureUrl;
    if (profilePicUrl) {
      profilePicUrl = `${profilePicUrl}${profilePicUrl.includes('?') ? '&' : '?'}v=${Date.now()}`;
    }
    setUser({
      id: payload.id,
      email: payload.email,
      firstName: payload.firstName,
      lastName: payload.lastName,
      userType: payload.userType,
      profilePictureUrl: profilePicUrl,
      emailConfirmed: payload.emailConfirmed
    });
    setIsAuthenticated(true);
    setAuthUnavailable(false);
    setError(null);
    // Fetch businesses after login so the BusinessSwitcher renders
    fetchBusinesses(payload.activeBusinessId);
  };

  const failure = (err: unknown, fallback: string): AuthResult => {
    const message = errorMessage(err, fallback);
    setError(message);
    return { ok: false, status: err instanceof ApiError ? err.status : undefined, message };
  };

  // Login function. Exactly ONE request per call: every attempt counts toward brute-force
  // protection, so a failure is reported back to the caller, never retried.
  const login = async (email: string, password: string, rememberMe: boolean, isMobileApp: boolean = false): Promise<AuthResult> => {
    setLoading(true);
    setError(null);

    try {
      const data = await requestJson<AuthResponsePayload>(AUTH_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0',
          'X-Requested-With': 'XMLHttpRequest' // Help prevent CSRF and clarify this is an AJAX request
        },
        credentials: 'include', // Include cookies in the request
        mode: 'cors',
        body: JSON.stringify({
          Email: email,
          Password: password,
          RememberMe: rememberMe,
          IsMobileApp: isMobileApp
        })
      });
      const signedIn = userFrom(data);
      if (!signedIn) return failure(null, SERVER_ERROR_MESSAGE);
      adoptUser(signedIn);
      return { ok: true, status: 200, message: 'Signed in.' };
    } catch (err) {
      return failure(err, 'Sign-in failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Register function — creates the account, auto-logs-in (cookie set by server),
  // and triggers a verification email. Reports whether email confirmation is still pending.
  const register = async (
    params: { firstName: string; lastName: string; email: string; password: string }
  ): Promise<RegisterResult> => {
    setLoading(true);
    setError(null);

    try {
      const registerUrl = import.meta.env.PROD
        ? `${getApiOrigin()}/api/auth/register`
        : AUTH_ENDPOINTS.REGISTER;

      const data = await requestJson<AuthResponsePayload & { emailConfirmationRequired?: boolean }>(registerUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        credentials: 'include',
        mode: 'cors',
        body: JSON.stringify(params)
      });

      // Server set the auth_token cookie — mark the session active and adopt the user.
      const registered = userFrom(data);
      if (!registered) return failure(null, SERVER_ERROR_MESSAGE);
      adoptUser(registered);
      return {
        ok: true,
        status: 200,
        message: 'Registration successful.',
        emailConfirmationRequired: data.emailConfirmationRequired === true,
      };
    } catch (err) {
      return failure(err, 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async (): Promise<void> => {
    setLoading(true);

    try {
      // In production, use the current origin
      const logoutUrl = import.meta.env.PROD
        ? `${getApiOrigin()}/api/auth/logout`
        : AUTH_ENDPOINTS.LOGOUT;

      // For HttpOnly cookies, we need to call the server to clear the cookie
      const response = await fetch(logoutUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        credentials: 'include' // Include cookies in the request
      });

      if (!response.ok) {
        // Logout failed but we still clear local state below
      }

      // Clear all auth state
      localStorage.removeItem('auth_token');
      sessionStorage.removeItem('auth_token');
      sessionStore.clearSession();
      // Don't leak the previous user's business selection into whatever session logs in next
      // on this browser.
      setActiveBusinessId(null);
      // Nor their data. Pages that cache their last payload in memory (the dashboard) would
      // otherwise render the previous user's rows for the frame before the next fetch lands.
      clearPageCache();

      // Reset user state
      setUser(null);
      setImpersonation({ active: false, by: null, readOnly: false });
      setIsAuthenticated(false);
      // Signed out is a known state: a stale "can't reach the server" must not outlive it.
      setAuthUnavailable(false);

      // Use history.pushState to change the URL without triggering a full page reload
      // This works better with React Router than directly manipulating window.location
      window.history.pushState({}, '', '/login');

      // Dispatch a popstate event to notify React Router of the URL change
      window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    } catch {
      // Even if there's an error, reset the local state
      setUser(null);
      setImpersonation({ active: false, by: null, readOnly: false });
      setIsAuthenticated(false);
      // Signed out is a known state: a stale "can't reach the server" must not outlive it.
      setAuthUnavailable(false);
      sessionStore.clearSession();
      setActiveBusinessId(null);
      // Same reason as the success path above: a failed logout request still ends the session
      // locally, so the cached pages must go with it.
      clearPageCache();

      // Use the same React Router compatible navigation approach
      window.history.pushState({}, '', '/login');
      window.dispatchEvent(new PopStateEvent('popstate', { state: {} }));
    } finally {
      setLoading(false);
    }
  };

  // Function to refresh user data
  const refreshUser = async (): Promise<void> => {
    await fetchUser();
  };

  // Context value with authentication functions
  const contextValue = {
    user,
    loading,
    isLoading: loading, // Alias for backward compatibility
    error,
    isAuthenticated,
    login,
    register,
    logout,
    getToken,
    refreshUser,
    setUser, // Add setUser function to allow components to update user state directly
    businesses,
    activeBusiness,
    switchBusiness,
    impersonation,
    authUnavailable,
    retryAuth: checkAuthStatus,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

